import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Drawer } from '@mui/material';
import { FiCheck, FiFilter, FiShoppingCart } from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { PrimaryButton, SecondaryButton } from '../../../shared/ui/Inputs';
import { Spinner } from '../../../shared/ui/Spinners';
import { WideLayout } from '../../../shared/layout/widelayout';
import { BasicPagination } from '../../../shared/ui/pagination/basic';
import { OrderFlowPageHeader } from '../../../shared/ui/orderFlowPageHeader';
import { ProductDataSource } from '../../../datasources/catalog/ProductsDataSource';
import { getProductsImages } from '../../../services/products/productService';
import { useSalesStore } from '../store/salesStore';
import { useSuballocationStore } from '../store/suballocationStore';
import { getBase64Img } from '../../../utils/format';
import { CartDrawer } from '../components/catalog/CartDrawer';
import { CatalogFilters } from '../components/catalog/CatalogFilters';
import { CatalogToolbar } from '../components/catalog/CatalogToolbar';
import { CategoryChips } from '../components/catalog/CategoryChips';
import { ProductCard } from '../components/catalog/ProductCard';
import { ProductModal } from '../components/catalog/ProductModal';
import {
  AddFeedback,
  CartButton,
  CatalogBody,
  LoadingWrapper,
  MobileFiltersBar,
  PageWrapper,
  ProductsGrid,
  SidebarColumn,
  StatePanel,
} from '../components/catalog/catalogStyles';

const PRODUCT_IMAGE_PLACEHOLDER = '/icones/placeholder-image.png';
const PAGE_SIZE_OPTIONS = [12, 24, 48];
const DISPOSABLE_SEGMENT_IDS = [1];
const SUBALLOCATION_SEGMENT_IDS = [1, 2, 3];
const productImagesCache = new Map();

const loadProductImages = async (sapCode) => {
  const key = String(sapCode ?? '');
  if (!key) return [];
  if (productImagesCache.has(key)) {
    return await productImagesCache.get(key);
  }

  const request = getProductsImages([key]).catch((error) => {
    productImagesCache.delete(key);
    throw error;
  });
  productImagesCache.set(key, request);
  return await request;
};

const productDescription = (product) => {
  const candidates = [
    product.explanation,
    product.explanation1,
    product.technical_information,
    product.description,
  ];
  return (
    candidates.find(
      (value) =>
        value !== undefined &&
        value !== null &&
        String(value).trim() !== '' &&
        String(value).trim() !== '0'
    ) ?? ''
  );
};

const validProductText = (value) => {
  const text = String(value ?? '').trim();
  return text && text !== '0' ? text : '';
};

const normalizeCatalogProduct = (product, images) => {
  return {
    id: product.id,
    sku: String(product.sap_code ?? ''),
    name: product.description ?? `Produto ${product.sap_code}`,
    category: product.product_category?.description ?? 'Sem categoria',
    subcategory: product.product_subcategory?.description ?? 'Sem subcategoria',
    description: productDescription(product),
    complementaryDescription: validProductText(product.explanation1),
    technicalInformation: validProductText(product.technical_information),
    images:
      Array.isArray(images) && images.length > 0
        ? images.map(getBase64Img)
        : [PRODUCT_IMAGE_PLACEHOLDER],
  };
};

const toFilterOptions = (values) =>
  (values ?? [])
    .filter(
      (item) =>
        item?.id !== undefined &&
        item?.id !== null &&
        String(item?.description ?? '').trim() !== ''
    )
    .map((item) => ({
      value: item.id,
      label: item.description,
      count: Number(item.count) || 0,
      categoryId: item?.category_id,
    }));

const toggleValue = (list, value) =>
  list.map(String).includes(String(value))
    ? list.filter((item) => String(item) !== String(value))
    : [...list, value];

const ProductCatalogPage = ({
  storeHook,
  catalogSegmentIds,
  title,
  patientPath,
  finishPath,
}) => {
  const selectedPatient = storeHook((state) => state.selectedPatient);
  const cart = storeHook((state) => state.cart);
  const filters = storeHook((state) => state.filters);
  const setFilters = storeHook((state) => state.setFilters);
  const addToCart = storeHook((state) => state.addToCart);
  const updateQuantity = storeHook((state) => state.updateQuantity);
  const removeFromCart = storeHook((state) => state.removeFromCart);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isProductModalOpen, setProductModalOpen] = useState(false);
  const [cartOpen, setCartOpen] = useState(false);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const [products, setProducts] = useState([]);
  const [facets, setFacets] = useState({
    categories: [],
    subcategories: [],
    segments: [],
  });
  const categoryIds = useMemo(
    () => (Array.isArray(filters.categoryIds) ? filters.categoryIds : []),
    [filters.categoryIds]
  );
  const subCategoryIds = useMemo(
    () => (Array.isArray(filters.subCategoryIds) ? filters.subCategoryIds : []),
    [filters.subCategoryIds]
  );
  const segmentIds = useMemo(
    () => (Array.isArray(filters.segmentIds) ? filters.segmentIds : []),
    [filters.segmentIds]
  );
  const searchFilter = filters.search ?? '';
  const [searchInput, setSearchInput] = useState(searchFilter);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(12);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [recentProductId, setRecentProductId] = useState(null);
  const [addFeedback, setAddFeedback] = useState('');
  const autoSelectionApplied = useRef(false);
  const navigate = useNavigate();
  const categoryOptions = useMemo(
    () => toFilterOptions(facets.categories),
    [facets.categories]
  );
  const subCategoryOptions = useMemo(
    () => toFilterOptions(facets.subcategories),
    [facets.subcategories]
  );
  const segmentOptions = useMemo(
    () => toFilterOptions(facets.segments),
    [facets.segments]
  );

  // Quando um facet possui uma única opção, ela é a seleção possível para
  // aquele catálogo e deve ser aplicada automaticamente.
  useEffect(() => {
    if (autoSelectionApplied.current) return;
    if (
      categoryOptions.length === 0 &&
      subCategoryOptions.length === 0 &&
      segmentOptions.length === 0
    ) {
      return;
    }

    const singleOptionFilters = {};
    if (categoryOptions.length === 1 && categoryIds.length === 0) {
      singleOptionFilters.categoryIds = [categoryOptions[0].value];
    }
    if (subCategoryOptions.length === 1 && subCategoryIds.length === 0) {
      singleOptionFilters.subCategoryIds = [subCategoryOptions[0].value];
    }
    if (segmentOptions.length === 1 && segmentIds.length === 0) {
      singleOptionFilters.segmentIds = [segmentOptions[0].value];
    }

    if (Object.keys(singleOptionFilters).length > 0) {
      setPage(0);
      setFilters(singleOptionFilters);
    }
    autoSelectionApplied.current = true;
  }, [
    categoryIds,
    categoryOptions,
    segmentIds,
    segmentOptions,
    setFilters,
    subCategoryIds,
    subCategoryOptions,
  ]);

  useEffect(() => {
    if (!selectedPatient) navigate(patientPath);
  }, [navigate, patientPath, selectedPatient]);

  useEffect(() => {
    let active = true;
    new ProductDataSource()
      .QuerySalesCatalogFilters({ catalogSegmentIds })
      .then((result) => {
        if (!active) return;
        setFacets({
          categories: result?.categories ?? [],
          subcategories: result?.subcategories ?? [],
          segments: result?.segments ?? [],
        });
      })
      .catch((loadError) => {
        if (active) {
          console.error('[SalesCatalog] Erro ao carregar filtros:', loadError);
        }
      });
    return () => {
      active = false;
    };
  }, [catalogSegmentIds]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput !== searchFilter) {
        setPage(0);
        setFilters({ search: searchInput });
      }
    }, 400);
    return () => clearTimeout(timer);
  }, [searchFilter, searchInput, setFilters]);

  useEffect(() => {
    if (!selectedPatient) return undefined;
    let active = true;
    setLoading(true);
    setError('');

    (async () => {
      try {
        const response = await new ProductDataSource().QuerySalesProducts({
          searchText: searchFilter,
          categoryIds,
          subCategoryIds,
          segmentIds,
          page,
          pageSize,
          catalogSegmentIds,
        });
        const rawProducts = Array.isArray(response?.data) ? response.data : [];
        const catalogProducts = rawProducts.map((product) =>
          normalizeCatalogProduct(product, [])
        );

        if (!active) return;
        setProducts(catalogProducts);
        setTotalItems(
          Number(
            response?.rows ??
              response?.totalItems ??
              response?.total ??
              response?.count ??
              rawProducts.length
          )
        );

        // As imagens vêm de outro endpoint e não devem bloquear a
        // renderização da página. Cada card é atualizado quando sua
        // foto estiver disponível.
        rawProducts.forEach((product) => {
          loadProductImages(product.sap_code)
            .then((images) => {
              if (!active || !Array.isArray(images) || images.length === 0) {
                return;
              }
              const normalizedImages = images.map(getBase64Img);
              setProducts((currentProducts) =>
                currentProducts.map((currentProduct) =>
                  currentProduct.id === product.id
                    ? {
                        ...currentProduct,
                        images: normalizedImages,
                      }
                    : currentProduct
                )
              );
            })
            .catch((imageError) => {
              console.warn(
                `[SalesCatalog] Imagem indisponível para ${product.sap_code}:`,
                imageError
              );
            });
        });
      } catch (loadError) {
        if (!active) return;
        console.error('[SalesCatalog] Erro ao carregar produtos:', loadError);
        setProducts([]);
        setTotalItems(0);
        setError('Não foi possível carregar os produtos. Tente novamente.');
      } finally {
        if (active) setLoading(false);
      }
    })();

    return () => {
      active = false;
    };
  }, [
    categoryIds,
    searchFilter,
    subCategoryIds,
    segmentIds,
    page,
    pageSize,
    catalogSegmentIds,
    selectedPatient,
    reloadToken,
  ]);

  const selectedQuantityByProductId = useMemo(
    () =>
      cart.reduce((quantities, item) => {
        quantities[item.productId] =
          (quantities[item.productId] ?? 0) + item.quantity;
        return quantities;
      }, {}),
    [cart]
  );
  const itemCount = cart.reduce((sum, item) => sum + item.quantity, 0);
  const hasActiveFilters =
    Boolean(searchFilter) ||
    categoryIds.length > 0 ||
    subCategoryIds.length > 0 ||
    segmentIds.length > 0;

  // Ao trocar as categorias, descarta as subcategorias que deixaram de
  // pertencer à seleção — o mesmo comportamento em cascata da versão antiga.
  const applyCategoryIds = useCallback(
    (values) => {
      const allowedCategoryIds = values.map(Number);
      const allowedSubCategoryIds = new Set(
        (facets.subcategories ?? [])
          .filter((subcategory) =>
            allowedCategoryIds.length === 0
              ? true
              : allowedCategoryIds.includes(Number(subcategory.category_id))
          )
          .map((subcategory) => Number(subcategory.id))
      );
      setPage(0);
      setFilters({
        categoryIds: values,
        subCategoryIds: subCategoryIds.filter((id) =>
          allowedSubCategoryIds.has(Number(id))
        ),
      });
    },
    [facets.subcategories, setFilters, subCategoryIds]
  );

  const currentSelection = useMemo(
    () => ({ categoryIds, subCategoryIds, segmentIds }),
    [categoryIds, subCategoryIds, segmentIds]
  );

  const handleToggleFilter = useCallback(
    (name, value) => {
      const nextValues = toggleValue(currentSelection[name] ?? [], value);

      if (name === 'categoryIds') {
        applyCategoryIds(nextValues);
        return;
      }

      setPage(0);
      setFilters({ [name]: nextValues });
    },
    [applyCategoryIds, currentSelection, setFilters]
  );

  const handleClearFilters = useCallback(() => {
    setPage(0);
    setSearchInput('');
    setFilters({
      search: '',
      categoryIds: [],
      subCategoryIds: [],
      segmentIds: [],
    });
  }, [setFilters]);

  const handleClearCategories = useCallback(() => {
    applyCategoryIds([]);
  }, [applyCategoryIds]);

  const handleAddToCart = (product, quantity = 1) => {
    addToCart(product, quantity);
    setRecentProductId(product.id);
    setAddFeedback(`${product.name} adicionado ao carrinho`);

    window.setTimeout(() => {
      setRecentProductId(null);
      setAddFeedback('');
    }, 1200);
  };

  const openProductModal = (product) => {
    setSelectedProduct(product);
    setProductModalOpen(true);
  };

  const filtersElement = (
    <CatalogFilters
      subCategoryOptions={subCategoryOptions}
      segmentOptions={segmentOptions}
      selected={currentSelection}
      onToggle={handleToggleFilter}
      onClear={handleClearFilters}
      hasActiveFilters={hasActiveFilters}
    />
  );

  return (
    <WideLayout
      outlet={
        <PageWrapper>
          <OrderFlowPageHeader
            title={`Catálogo de ${title.toLowerCase()}`}
            subtitle={`Selecione os produtos para ${
              selectedPatient?.name ?? 'o paciente'
            }.`}
          />

          <CatalogToolbar
            searchValue={searchInput}
            onSearchChange={setSearchInput}
            totalItems={totalItems}
            showResults={!loading && !error}
          />

          <CategoryChips
            options={categoryOptions}
            selected={categoryIds}
            onToggle={(value) => handleToggleFilter('categoryIds', value)}
            onClear={handleClearCategories}
          />

          <CatalogBody>
            <SidebarColumn>{filtersElement}</SidebarColumn>

            <div>
              <MobileFiltersBar>
                <SecondaryButton
                  onClick={() => setMobileFiltersOpen(true)}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '8px',
                  }}
                >
                  <FiFilter /> Filtros
                </SecondaryButton>
              </MobileFiltersBar>

              {loading ? (
                <LoadingWrapper>
                  <Spinner />
                </LoadingWrapper>
              ) : error ? (
                <StatePanel>
                  <p>{error}</p>
                  <PrimaryButton
                    onClick={() => setReloadToken((value) => value + 1)}
                  >
                    Tentar novamente
                  </PrimaryButton>
                </StatePanel>
              ) : products.length === 0 ? (
                <StatePanel>
                  Nenhum produto encontrado para os filtros selecionados.
                </StatePanel>
              ) : (
                <>
                  <ProductsGrid>
                    {products.map((product) => (
                      <ProductCard
                        key={product.id}
                        product={product}
                        added={recentProductId === product.id}
                        selectedQuantity={
                          selectedQuantityByProductId[product.id] ?? 0
                        }
                        onOpen={openProductModal}
                        onAdd={handleAddToCart}
                      />
                    ))}
                  </ProductsGrid>
                  <BasicPagination
                    qtyItems={totalItems}
                    page={page}
                    pageSize={pageSize}
                    pageSizeOptions={PAGE_SIZE_OPTIONS}
                    onChange={(pagination) => {
                      setPage(Number(pagination.page));
                      setPageSize(Number(pagination.pageSize));
                    }}
                    style={{ marginTop: '24px' }}
                  />
                </>
              )}
            </div>
          </CatalogBody>

          <Drawer
            anchor="left"
            open={mobileFiltersOpen}
            onClose={() => setMobileFiltersOpen(false)}
          >
            <div
              style={{
                width: 'min(300px, 88vw)',
                padding: '16px',
                boxSizing: 'border-box',
              }}
            >
              {filtersElement}
            </div>
          </Drawer>

          {addFeedback && (
            <AddFeedback role="status">
              <FiCheck /> {addFeedback}
            </AddFeedback>
          )}
          <CartButton
            type="button"
            aria-label={`Abrir carrinho com ${itemCount} item${
              itemCount === 1 ? '' : 's'
            }`}
            onClick={() => setCartOpen(true)}
            $pulsing={Boolean(recentProductId)}
          >
            <FiShoppingCart />
            {itemCount > 0 && <span>{itemCount}</span>}
          </CartButton>
          <ProductModal
            product={selectedProduct}
            open={isProductModalOpen}
            onClose={() => setProductModalOpen(false)}
            onExited={() => setSelectedProduct(null)}
            onAdd={handleAddToCart}
          />
          <CartDrawer
            open={cartOpen}
            onClose={() => setCartOpen(false)}
            cart={cart}
            onUpdateQuantity={updateQuantity}
            onRemove={removeFromCart}
            onConfirm={() => {
              setCartOpen(false);
              navigate(finishPath);
            }}
          />
        </PageWrapper>
      }
    />
  );
};

export const SalesCatalogPage = () => (
  <ProductCatalogPage
    storeHook={useSalesStore}
    catalogSegmentIds={DISPOSABLE_SEGMENT_IDS}
    title="Descartáveis"
    patientPath="/sales/selectPatient"
    finishPath="/sales/finish"
  />
);

export const SuballocationCatalogPage = () => (
  <ProductCatalogPage
    storeHook={useSuballocationStore}
    catalogSegmentIds={SUBALLOCATION_SEGMENT_IDS}
    title="Sublocação"
    patientPath="/suballocation/selectPatient"
    finishPath="/suballocation/finish"
  />
);
